import { useNavigate, useParams } from "react-router-dom";
import { useAsyncData } from "../../../hooks/useAsyncData";
import { usePolling } from "../../../hooks/usePolling";
import { useConfirmHandler, type UseConfirmHandlerResult } from "../../../hooks/useConfirmHandler";
import {
  cancelBatchJob,
  deleteBatchJob,
  getBatchJob,
  pauseBatchJob,
  resumeBatchJob,
  retryFailedBatchJobItems,
  type BatchJob,
} from "../../../lib/corpusApi";
import { ROUTES } from "../../../route_paths";

const POLL_INTERVAL_MS = 30_000;
const TERMINAL_STATUSES = new Set<BatchJob["status"]>(["completed", "failed", "cancelled"]);

export interface UseBatchJobDetailResult {
  job: BatchJob | null;
  loading: boolean;
  error: string | null;
  cancelDialogProps: UseConfirmHandlerResult["dialogProps"];
  onCancel: () => void;
  /** Pauses the job, so the next queued job of its kind takes over. */
  onPause: () => Promise<void>;
  /** Queues a paused job again, to run once no other job of its kind is running. */
  onResume: () => Promise<void>;
  /** Deletes the job (the UI calls it archiving) and returns to the job list. */
  onDelete: () => Promise<void>;
  /** Puts the job's failed items back in line, reopening the job if it had completed. */
  onRetryFailed: () => Promise<void>;
  refresh: () => Promise<void>;
}

/** Fetches one batch job and keeps refreshing it while it's still queued, running or paused,
 *  stopping once the job reaches a terminal status. */
export function useBatchJobDetail(): UseBatchJobDetailResult {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: job, loading, error, refresh } = useAsyncData(() => getBatchJob(id!));

  // useAsyncData already fetched on mount, so polling starts one interval after the job loads.
  const isPolling = job !== null && !TERMINAL_STATUSES.has(job.status);
  usePolling(refresh, POLL_INTERVAL_MS, { enabled: isPolling, leading: false });

  const confirm = useConfirmHandler({
    title: "Cancel batch job",
    message:
      "Items already processed keep their results. Items not yet processed are left untouched.",
    confirmLabel: "Cancel job",
    confirmColor: "error",
  });

  function onCancel() {
    confirm.handle(async () => {
      await cancelBatchJob(id!);
      await refresh();
    });
  }

  async function onPause() {
    await pauseBatchJob(id!);
    await refresh();
  }

  async function onResume() {
    await resumeBatchJob(id!);
    await refresh();
  }

  async function onDelete() {
    await deleteBatchJob(id!);
    navigate(ROUTES.internal.batchJobs.path());
  }

  async function onRetryFailed() {
    await retryFailedBatchJobItems(id!);
    await refresh();
  }

  return {
    job,
    loading,
    error,
    cancelDialogProps: confirm.dialogProps,
    onCancel,
    onPause,
    onResume,
    onDelete,
    onRetryFailed,
    refresh,
  };
}
